import type { GameplayAction } from './InputAction';

interface BufferedAction {
  readonly action: GameplayAction;
  /** Simulation time at which the input arrived (seconds). */
  readonly time: number;
}

/**
 * Keeps gameplay inputs alive for a short window. A jump pressed 100 ms
 * before landing still fires on landing – essential for responsive controls.
 * Uses simulation time, so buffering is deterministic and pause-safe.
 */
export class InputBuffer {
  private readonly entries: BufferedAction[] = [];

  constructor(private readonly bufferTime: number) {}

  push(action: GameplayAction, time: number): void {
    this.entries.push({ action, time });
  }

  /**
   * Offers every non-expired action (oldest first) to `execute`.
   * Actions for which `execute` returns true are consumed; others stay buffered.
   */
  process(now: number, execute: (action: GameplayAction) => boolean): void {
    this.prune(now);
    for (let i = 0; i < this.entries.length; ) {
      const entry = this.entries[i] as BufferedAction;
      if (execute(entry.action)) this.entries.splice(i, 1);
      else i++;
    }
  }

  prune(now: number): void {
    for (let i = this.entries.length - 1; i >= 0; i--) {
      if (now - (this.entries[i] as BufferedAction).time > this.bufferTime) this.entries.splice(i, 1);
    }
  }

  clear(): void {
    this.entries.length = 0;
  }

  get size(): number {
    return this.entries.length;
  }
}
